import { useQuery, useMutation, useQueryClient } from 'react-query'
import { ajudaCustoService } from '../services/ajudaCusto'
import { SolicitacaoFiltros } from '../tipos/ajudaCusto'
import { useToast } from '../../../contexts/NotificationContext'

function extrairMensagem(error: any, fallback: string): string {
  const detalhes = error?.response?.data
  if (typeof detalhes === 'string') return detalhes
  return error?.message || fallback
}

// ---------- Calendário / Registro ----------

export function useCalendario(ano: number, mes: number) {
  const toast = useToast()
  return useQuery(['ajuda-custo-calendario', ano, mes], () => ajudaCustoService.obterCalendario(ano, mes), {
    keepPreviousData: true,
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao carregar calendário')) },
  })
}

export function useRegistrarHoje() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(() => ajudaCustoService.registrarHoje(), {
    onSuccess: () => {
      toast.success('Registro efetuado com sucesso!')
      queryClient.invalidateQueries(['ajuda-custo-calendario'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao registrar')) },
  })
}

export function useSolicitarAjuste() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(
    ({ data, justificativa }: { data: string; justificativa: string }) =>
      ajudaCustoService.solicitarAjuste(data, justificativa),
    {
      onSuccess: () => {
        toast.success('Solicitação enviada aos administradores!')
        queryClient.invalidateQueries(['ajuda-custo-calendario'])
      },
      onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao solicitar ajuste')) },
    },
  )
}

// ---------- Manutenção ----------

export function useSolicitacoes(filtros?: SolicitacaoFiltros) {
  const toast = useToast()
  return useQuery(['ajuda-custo-solicitacoes', filtros], () => ajudaCustoService.obterSolicitacoes(filtros), {
    keepPreviousData: true,
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao carregar solicitações')) },
  })
}

export function useAprovarSolicitacao() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation((id: number) => ajudaCustoService.aprovarSolicitacao(id), {
    onSuccess: () => {
      toast.success('Solicitação aprovada!')
      queryClient.invalidateQueries(['ajuda-custo-solicitacoes'])
      queryClient.invalidateQueries(['ajuda-custo-calendario'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao aprovar')) },
  })
}

export function useReprovarSolicitacao() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(({ id, motivo }: { id: number; motivo: string }) => ajudaCustoService.reprovarSolicitacao(id, motivo), {
    onSuccess: () => {
      toast.success('Solicitação reprovada!')
      queryClient.invalidateQueries(['ajuda-custo-solicitacoes'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao reprovar')) },
  })
}

export function useLancarRegistroManual() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(({ pessoaId, data }: { pessoaId: number; data: string }) => ajudaCustoService.lancarRegistroManual(pessoaId, data), {
    onSuccess: () => {
      toast.success('Registro lançado com sucesso!')
      queryClient.invalidateQueries(['ajuda-custo-solicitacoes'])
      queryClient.invalidateQueries(['ajuda-custo-calendario'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao lançar registro')) },
  })
}

// ---------- Relatório ----------

export function useLotes() {
  const toast = useToast()
  return useQuery(['ajuda-custo-lotes'], () => ajudaCustoService.obterLotes(), {
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao carregar lotes')) },
  })
}

export function useGerarLote() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(
    ({ dataInicio, dataFim, pessoaId }: { dataInicio: string; dataFim: string; pessoaId?: number }) =>
      ajudaCustoService.gerarLote(dataInicio, dataFim, pessoaId),
    {
      onSuccess: () => {
        toast.success('Lote gerado e pessoas notificadas!')
        queryClient.invalidateQueries(['ajuda-custo-lotes'])
      },
      onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao gerar lote')) },
    },
  )
}

// ---------- Parametrização ----------

export function useValoresDiaria() {
  const toast = useToast()
  return useQuery(['ajuda-custo-valores-diaria'], () => ajudaCustoService.obterValoresDiaria(), {
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao carregar parametrizações')) },
  })
}

export function useCriarValorDiaria() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation((dto: { valor: number; dataInicioVigencia: string; dataFimVigencia: string }) => ajudaCustoService.criarValorDiaria(dto), {
    onSuccess: () => {
      toast.success('Parametrização criada!')
      queryClient.invalidateQueries(['ajuda-custo-valores-diaria'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao criar parametrização')) },
  })
}

export function useAtualizarValorDiaria() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation(
    ({ id, dto }: { id: number; dto: { valor: number; dataInicioVigencia: string; dataFimVigencia: string } }) =>
      ajudaCustoService.atualizarValorDiaria(id, dto),
    {
      onSuccess: () => {
        toast.success('Parametrização atualizada!')
        queryClient.invalidateQueries(['ajuda-custo-valores-diaria'])
      },
      onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao atualizar parametrização')) },
    },
  )
}

export function useExcluirValorDiaria() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation((id: number) => ajudaCustoService.excluirValorDiaria(id), {
    onSuccess: () => {
      toast.success('Parametrização excluída!')
      queryClient.invalidateQueries(['ajuda-custo-valores-diaria'])
    },
    onError: (error: any) => { toast.error(extrairMensagem(error, 'Erro ao excluir parametrização')) },
  })
}

export function usePessoasAjudaCusto() {
  return useQuery(['ajuda-custo-pessoas'], () => ajudaCustoService.listarPessoas(), {
    staleTime: 0,
    refetchOnMount: true,
  })
}
